import { useMutation, useQuery } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { groupBy } from "es-toolkit/compat";
import { useMemo, useState } from "react";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { storage } from "@/modules/storage";
import { isMembershipRewardAvailable } from "@/modules/wallet/membershipRewardAvailability";
import {
  getAuthClaims,
  getPlatform,
  getPlatformLocation,
  getPlatformLocations,
  getPlatformUserMemberships,
  getPlatformUserPackages,
  getUser,
  getUserCheckIn,
  postPlatformUserPackageItemsRedeemInApp,
} from "api/methods";
import { Button } from "atoms/button/Button";
import { PastTreatmentCard } from "molecules/containers/past-treatment-card/PastTreatmentCard";
import { TreatmentCard } from "molecules/containers/treatment-card/TreatmentCard";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { NoTreatmentsContainer } from "organisms/treatment-list/TreatmentList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getBookAppointmentAction, getProductTitle } from "services/HelperService";
import { waitObj } from "toolbox/Promise";
import { openUrl } from "utils/openUrl/openUrl";

const load = async () => {
  const userInfo = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 0,
  });

  const platform = queryClient.fetchQuery({ ...getPlatform.keyed() });

  const myTreatments = queryClient.fetchQuery({ ...getPlatformUserPackages.keyed() });

  const memberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  const locations = queryClient
    .fetchQuery({ ...getPlatformLocations.keyed({}) })
    .then(({ items }) => items);

  const platformLocation = queryClient.fetchQuery({ ...getPlatformLocation.keyed() });

  const checkInAvailable = queryClient.fetchQuery({ ...getUserCheckIn.keyed() });

  const authClaims = queryClient.fetchQuery({ ...getAuthClaims.keyed() });

  return waitObj({
    platform,
    myTreatments,
    memberships,
    userInfo,
    locations,
    platformLocation,
    checkInAvailable,
    authClaims,
  });
};

export const prefetchWalletTreatments = () => {
  void load().catch(() => undefined);
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletTreatmentsLoader: LoaderFunction = async () => {
  const data = await load();
  if (
    !data.platform.platformSettings?.marketplaceEnabled &&
    !data.myTreatments.length &&
    !data.memberships.length
  ) {
    return redirect("/wallet");
  }
  return data;
};

const WalletTreatmentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loaderData = useLoaderData() as LoaderData;

  // Use query for reactive updates to treatments
  const { data: myTreatments = loaderData.myTreatments } = useQuery({
    ...getPlatformUserPackages.keyed(),
    initialData: loaderData.myTreatments,
  });

  const { platform, memberships, locations, checkInAvailable, authClaims } = loaderData;
  const isUnitedDerm = useFeatureFlag("unitedDerm");

  // Confetti from card position
  const triggerConfettiFromCard = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    const colors = [
      "#D4AF37",
      "#CFB53B",
      "#F0E68C",
      "#DAA520",
      "#C9B037",
      "#B8860B",
      "#F4E4C1",
      "#E6C794",
    ];

    const defaults = {
      origin: { x, y },
      colors,
      ticks: 200,
      gravity: 1,
      drift: 0,
      startVelocity: 35,
      shapes: ["circle", "square"] as confetti.Shape[],
      scalar: 1,
    };

    const particleCount = 50;
    confetti({
      ...defaults,
      particleCount,
      spread: 70,
    });

    confetti({
      ...defaults,
      particleCount: particleCount / 2,
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
  };

  // Check if user can skip scanning (admin or checked in)
  const isAdminImpersonation = !!authClaims?.claims?.adminUserId;
  const skipScanInOffice = !checkInAvailable || isAdminImpersonation;
  const manualRedemptionOnly = platform.platformSettings?.manualRedemptionOnlyEnabled ?? false;

  // Location state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Redeem state for v3
  const [treatmentShowingQuantity, setTreatmentShowingQuantity] = useState<string | null>(null);
  const [treatmentShowingSelector, setTreatmentShowingSelector] = useState<string | null>(null);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<string | null>(null);

  // Persist redeemed today in storage with local date key
  const getStorageKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `redeemed-today-${year}-${month}-${day}`;
  };

  // Clean up old storage keys on mount
  const cleanupOldStorageKeys = () => {
    const currentKey = getStorageKey();
    storage
      .keys()
      .filter((key) => key.startsWith("redeemed-today-") && key !== currentKey)
      .forEach((key) => storage.removeItem(key));
  };

  const [redeemedToday, setRedeemedToday] = useState<Record<string, number>>(() => {
    cleanupOldStorageKeys();
    try {
      const stored = storage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Redeem mutation for direct redemption
  const redeemMutation = useMutation({
    mutationFn: ({
      packageId,
      locationId,
      isFromMembership,
      productId,
      quantity,
    }: {
      packageId: string;
      locationId?: string;
      isFromMembership: boolean;
      productId: string;
      quantity: number;
    }) =>
      postPlatformUserPackageItemsRedeemInApp({
        packageId,
        locationId,
        isFromMembership,
        productId,
        quantity,
      }),
    onSuccess: async (_, variables) => {
      // Track redeemed items - confetti will trigger from card via useEffect
      setRedeemedToday((prev) => {
        const updated = {
          ...prev,
          [variables.packageId]: (prev[variables.packageId] || 0) + variables.quantity,
        };
        // Persist to storage
        try {
          storage.setItem(getStorageKey(), JSON.stringify(updated));
        } catch (_) {
          // NOSONAR — localStorage may be unavailable (e.g. private browsing)
        }
        return updated;
      });

      // Force refetch to get updated data
      await queryClient.invalidateQueries({
        queryKey: getPlatformUserPackages.key(),
      });
      await queryClient.refetchQueries({
        queryKey: getPlatformUserPackages.key(),
        type: "active",
      });

      setTreatmentShowingQuantity(null);
      setTreatmentShowingSelector(null);
      setSelectedTreatmentId(null);
    },
    onError: (error: any) => {
      // Silently handle error and reset state
      setTreatmentShowingQuantity(null);
      setTreatmentShowingSelector(null);
      setSelectedTreatmentId(null);
    },
  });

  const handleRedeemButtonClick = (treatment: (typeof myTreatments)[0]) => {
    // Check if multiple items in package
    if (treatment.platformUserPackageItemModels.length > 1) {
      // Show treatment selector first
      setTreatmentShowingSelector(treatment.packageId);
      setSelectedTreatmentId(null);
    } else {
      // Single treatment - check quantity
      const itemModel = treatment.platformUserPackageItemModels[0];
      if (itemModel && itemModel.remainingQuantity > 1) {
        setTreatmentShowingQuantity(treatment.packageId);
        setSelectedTreatmentId(itemModel.productId);
      } else {
        // Just redeem 1
        handleRedeem(treatment, 1, itemModel?.productId);
      }
    }
  };

  const handleTreatmentSelected = (
    treatment: (typeof myTreatments)[0],
    selections: Record<string, number>,
  ) => {
    // Now we handle multi-select redemption
    // selections is a map of productId -> quantity
    setTreatmentShowingSelector(null);

    // Process each selection
    Object.entries(selections).forEach(([productId, quantity]) => {
      if (quantity > 0) {
        handleRedeem(treatment, quantity, productId);
      }
    });
  };

  const handleRedeem = (
    treatment: (typeof myTreatments)[0],
    quantity: number,
    productId?: string,
  ) => {
    const isMemberBenefit = treatment.type === "MembershipTreatment";
    const product = productId || treatment.platformUserPackageItemModels[0]?.productId;

    if (!product) {
      return;
    }

    if (skipScanInOffice) {
      // Can redeem directly
      // For membership treatments, use the treatment's locationId
      // For purchased packages, don't send locationId (let backend determine it)
      redeemMutation.mutate({
        packageId: treatment.packageId,
        locationId: isMemberBenefit ? (treatment.locationId ?? undefined) : undefined,
        isFromMembership: isMemberBenefit,
        productId: product,
        quantity,
      });
    } else {
      // Navigate to scan page
      navigate("/scan-redeem-treatment-in-location", {
        state: {
          packageId: treatment.packageId,
          isFromMembership: isMemberBenefit.toString(),
          productId: product,
          remainingAmount: treatment.remainingTreatmentAmount - quantity,
          quantity,
          locationId: treatment.locationId,
        },
      });
    }
  };

  const availableLocationIds = myTreatments
    ?.map((t) => t.locationId)
    .filter((id): id is string => typeof id === "string");

  const filterPlatformLocationsBasedOnAvailableTreatments = (
    availableLocationIds?: string[] | null,
    platformLocations?: typeof locations,
  ) => {
    return (
      (availableLocationIds?.length
        ? platformLocations?.filter(({ id }) => availableLocationIds.includes(id))
        : platformLocations) ?? []
    );
  };

  const filteredLocations = filterPlatformLocationsBasedOnAvailableTreatments(
    availableLocationIds,
    locations,
  );

  // Handles legacy locations that may not have url, email, or phone number
  const bookablePlatformLocations = filteredLocations.filter((location) =>
    getBookAppointmentAction(location.bookAppointmentUrl, location.phoneNumber, location.email),
  );
  const locationMap = new Map(filteredLocations?.map((location) => [location.id, location]));

  const handleBookAppointmentClick = () => {
    if (bookablePlatformLocations.length === 1) {
      const [location] = bookablePlatformLocations;
      const href = getBookAppointmentAction(
        location.bookAppointmentUrl,
        location.phoneNumber,
        location.email,
      );
      if (href) {
        openUrl(href);
      }
    } else if (bookablePlatformLocations?.length > 1) {
      setSelectedLocationId(""); // Reset selection
      setIsLocationModalOpen(true);
    }
    // If there are 0 valid locations, do nothing (button won't be shown anyway)
  };

  // Count active treatments (those with remaining treatments)
  const activeTreatmentsCount = myTreatments.filter(
    (treatment) => treatment.remainingTreatmentAmount > 0,
  ).length;

  const locationsIdToNameMap = new Map(filteredLocations?.map(({ id, name }) => [id, name]));

  // Helper to get membership name from reward group IDs
  const getMembershipName = (membershipRewardGroupIds?: string[] | null): string | null => {
    if (!membershipRewardGroupIds || membershipRewardGroupIds.length === 0) {
      return null;
    }

    const membership = memberships.find(({ membership }) =>
      membership.membershipRewardGroups?.some((rg) => membershipRewardGroupIds.includes(rg.id)),
    );

    return membership?.membership.name ?? null;
  };

  // Memoize reward groups to avoid recalculating on every filter iteration
  const allRewardGroups = useMemo(
    () => memberships.flatMap(({ membership }) => membership.membershipRewardGroups ?? []),
    [memberships],
  );

  // Separate active and past treatments for v3, sort membership treatments to top
  const activeTreatments = myTreatments
    .filter((treatment) => {
      // Must have remaining treatments to be active
      if (treatment.remainingTreatmentAmount <= 0) {
        return false;
      }
      // For membership treatments: must also be active flag set
      if (treatment.type === "MembershipTreatment" && !treatment.active) {
        return false;
      }
      // Check membership reward availability (by rewardGroupIds or packageId)
      return isMembershipRewardAvailable(treatment, allRewardGroups);
    })
    .sort((a, b) => {
      // Sort membership treatments first
      const aIsMember = a.type === "MembershipTreatment" ? 0 : 1;
      const bIsMember = b.type === "MembershipTreatment" ? 0 : 1;
      return aIsMember - bIsMember;
    });

  const pastTreatments = myTreatments
    .filter((treatment) => {
      // For membership treatments: show as past if inactive (cancelled/expired)
      if (treatment.type === "MembershipTreatment") {
        return !treatment.active;
      }
      // For regular purchased treatments: show as past if fully redeemed
      return treatment.remainingTreatmentAmount === 0;
    })
    .sort((a, b) => {
      // Sort membership treatments first
      const aIsMember = a.type === "MembershipTreatment" ? 0 : 1;
      const bIsMember = b.type === "MembershipTreatment" ? 0 : 1;
      return aIsMember - bIsMember;
    });

  // Group treatments by location for v3
  const activeTreatmentsByLocation = groupBy(activeTreatments, "locationId");
  const pastTreatmentsByLocation = groupBy(pastTreatments, "locationId");

  const isPartnerEmrBookingEnabled = platform.platformSettings?.partnerEmrBookingEnabled;

  return (
    <div className="flex grow flex-col overflow-auto">
      {/* V3 Layout - Credit card style for active, smaller cards for past */}
      {activeTreatments.length === 0 && pastTreatments.length === 0 && (
        <div className="mx-4 mt-4 overflow-clip rounded-lg">
          <NoTreatmentsContainer text={t("walletTreatmentsPage.noTreatments")} />
        </div>
      )}

      {/* Active Treatments - Grouped by Location */}
      {activeTreatments.length > 0 && (
        <>
          {Object.entries(activeTreatmentsByLocation).map(([locationId, locationTreatments]) => {
            const locationName =
              locationId === "null" ? null : locationsIdToNameMap.get(locationId);

            return (
              <div key={locationId} className="pb-6 pt-4">
                {locationName && (
                  <div className="mb-4 px-4">
                    <span className="text-body3 font-medium text-secondary">{locationName}</span>
                  </div>
                )}
                <div className="flex flex-col gap-5 px-4">
                  {locationTreatments.map((treatment) => {
                    const isMemberBenefit = treatment.type === "MembershipTreatment";
                    const membershipName = isMemberBenefit
                      ? getMembershipName(treatment.membershipRewardGroupIds)
                      : null;
                    const isShowingQuantity = treatmentShowingQuantity === treatment.packageId;
                    const isShowingTreatmentSelector =
                      treatmentShowingSelector === treatment.packageId;
                    const itemModel = selectedTreatmentId
                      ? treatment.platformUserPackageItemModels.find(
                          (item) => item.productId === selectedTreatmentId,
                        )
                      : treatment.platformUserPackageItemModels[0];
                    const maxQuantity = itemModel?.remainingQuantity ?? 1;
                    const isRedeeming = redeemMutation.isPending;
                    const redeemedCount = redeemedToday[treatment.packageId] || 0;

                    const isBookableService =
                      treatment.platformUserPackageItemModels.length === 1 &&
                      isPartnerEmrBookingEnabled;
                    const hideRedeemButton =
                      isBookableService || isAdminImpersonation || manualRedemptionOnly;

                    return (
                      <TreatmentCard
                        key={treatment.packageId}
                        id={treatment.packageId}
                        imgUrl={treatment.imageUrl}
                        type={treatment.type}
                        title={getProductTitle(treatment.productName, treatment.variantName)}
                        remainingTreatmentsCount={treatment.remainingTreatmentAmount}
                        packageUnitType={treatment.packageUnitType}
                        membershipName={membershipName}
                        isMemberBenefit={isMemberBenefit}
                        showQuantitySelector={isShowingQuantity}
                        maxQuantity={maxQuantity}
                        isRedeeming={isRedeeming}
                        redeemedToday={redeemedCount}
                        onConfettiTrigger={triggerConfettiFromCard}
                        showTreatmentSelector={isShowingTreatmentSelector}
                        treatments={treatment.platformUserPackageItemModels.map((item) => ({
                          id: item.productId,
                          name: item.name,
                          remainingQuantity: item.remainingQuantity,
                        }))}
                        onTreatmentSelect={(selections) =>
                          handleTreatmentSelected(treatment, selections)
                        }
                        onClick={() => {
                          if (!isRedeeming) {
                            navigate(
                              `${treatment.packageId}?isFromMembership=${isMemberBenefit}&active=true`,
                            );
                          }
                        }}
                        onRedeem={
                          hideRedeemButton
                            ? undefined
                            : (quantity) => {
                                if (isRedeeming) return;

                                if (isShowingQuantity) {
                                  // Redeem with selected quantity
                                  handleRedeem(treatment, quantity, itemModel?.productId);
                                } else {
                                  // First click - check if we need quantity selector
                                  handleRedeemButtonClick(treatment);
                                }
                              }
                        }
                        showRemainingQuantity={!isBookableService}
                        onCancelQuantitySelector={() => {
                          setTreatmentShowingQuantity(null);
                          setTreatmentShowingSelector(null);
                          setSelectedTreatmentId(null);
                        }}
                        onBookAppointment={
                          bookablePlatformLocations.length > 0 &&
                          !isPartnerEmrBookingEnabled &&
                          !isAdminImpersonation
                            ? (e) => {
                                e.stopPropagation();
                                handleBookAppointmentClick();
                              }
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Past Treatments - Grouped by Location */}
      {pastTreatments.length > 0 && (
        <>
          {Object.entries(pastTreatmentsByLocation).map(([locationId, locationTreatments]) => {
            const locationName =
              locationId === "null" ? null : locationsIdToNameMap.get(locationId);

            return (
              <div key={locationId} className="pb-6">
                <div className="mb-4 px-4">
                  <h2 className="text-sub2 uppercase text-secondary">
                    {t("walletTreatmentsPage.pastTreatmentsHeader")}
                  </h2>
                  {locationName && (
                    <span className="text-body3 font-medium text-secondary">{locationName}</span>
                  )}
                </div>
                <div className="flex flex-col gap-3 px-4">
                  {locationTreatments.map((treatment) => {
                    const isMemberBenefit = treatment.type === "MembershipTreatment";
                    const membershipName = isMemberBenefit
                      ? getMembershipName(treatment.membershipRewardGroupIds)
                      : null;

                    return (
                      <PastTreatmentCard
                        key={treatment.packageId}
                        id={treatment.packageId}
                        imgUrl={treatment.imageUrl}
                        type={treatment.type}
                        title={getProductTitle(treatment.productName, treatment.variantName)}
                        remainingTreatmentsCount={treatment.remainingTreatmentAmount}
                        packageUnitType={treatment.packageUnitType}
                        membershipName={membershipName}
                        isMemberBenefit={isMemberBenefit}
                        onClick={() => {
                          navigate(
                            `${treatment.packageId}?isFromMembership=${isMemberBenefit}&active=false`,
                          );
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {!isPartnerEmrBookingEnabled &&
        bookablePlatformLocations.length > 0 &&
        activeTreatments.length > 0 && (
          <div className="pb-safe mt-4 bg-one p-4">
            <Button size="medium" style="primary" fullWidth onClick={handleBookAppointmentClick}>
              {isUnitedDerm
                ? t("walletTreatmentsPage.requestAppointment")
                : t("walletTreatmentsPage.bookAppointment")}
            </Button>
          </div>
        )}

      {/* Extra bottom padding to ensure content is scrollable past bottom nav */}
      <div className="h-20" />

      <ModalSelectorList
        size="medium"
        open={isLocationModalOpen}
        title={
          activeTreatmentsCount === 1
            ? t("walletTreatmentsPage.whereBookServiceSingular")
            : t("walletTreatmentsPage.whereBookServicePlural")
        }
        type="location"
        items={bookablePlatformLocations.map(({ id, name, city, state, postalCode, address1 }) => ({
          id,
          title: name ?? "",
          description: `${city}, ${state}, ${postalCode}`,
          subtitle: address1 ?? undefined,
        }))}
        checkedItems={[selectedLocationId]}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onFirstButtonClick={() => {
          const selectedLocation = locationMap.get(selectedLocationId);

          const href = selectedLocation
            ? getBookAppointmentAction(
                selectedLocation.bookAppointmentUrl,
                selectedLocation.phoneNumber,
                selectedLocation.email,
              )
            : null;
          if (href) {
            openUrl(href);
          }

          setIsLocationModalOpen(false);
        }}
        firstButtonLabel={t("walletTreatmentsPage.bookHere")}
        firstButtonDisabled={!selectedLocationId}
        onSelect={(locationId) => setSelectedLocationId(locationId)}
      />
    </div>
  );
};

export default WalletTreatmentsPage;
